import React, {useEffect, useState} from 'react';
import bn from "../utils/bemnames";
import {fetchBanners, fetchWidgetData as fetchMatchCenterWidgetData, IWidgetResponse} from '../api/match-center'
import {_t, setUpTranslations} from '../utils/translations';
import {getMeta, setUpWidgetMetas, WidgetMetaTypes} from '../utils/widget-meta';
import {BuilderComponentSlug, IBannerAdObj} from '../utils/types';
import Standings from '../components/Standings/Standings';
import {applyCustomStyling} from '../utils/helpers';
import {setBannerAdInfo, setPositionOrder} from '../utils/match-center';
import {setAllBuilderComponentStatuses} from '../utils/menu';
import AdBanner from '../components/MatchCenter/AdBanner';
import OddsComparison from './OddsComparison';
import OutrightOdds from '../components/Sports/OutRightOdds/OutRightOdds';
import NextRoundBanner from "../components/NextRoundBanner";
import FeaturedEvents from '../components/FeaturedEvents';
import useCheckContainerWidth from "../utils/useCheckContainerWidth";
import MatchSchedule from '../components/LeagueCenter/MatchSchedule';
import LeagueCountdown from '../components/LeagueCountdown';
import {useGlobalState} from "../contexts/GlobalState";
import useCheckMobileScreen from "../hooks/useCheckMobileScreen";
import {fetchPromotedProvider} from "../api/general";
import {MCStyleVariable, setStyleVariable} from "../utils/widget-style";
import LoadingBlock from '../components/LoadingBlock';
import {setWidgetProviders} from '../utils/widget-providers';

const bem = bn.create('league-center');

interface IProps {
  widgetID: number;
  competitionId: null | number;
  listRangeFrom?: number|null;
  listRangeTo?: number|null;
  marketTypeIds?: number[]|null;
  elem: Element;
  hasCache: boolean;
}

const LeagueCenter: React.FC<IProps> = ({
  widgetID,
  competitionId,
  elem,
  listRangeFrom = null,
  listRangeTo = null,
  marketTypeIds = null,
  hasCache
}) => {
  const [loading, setLoading] = useState<Boolean>(true)
  const [requestFailed, setRequestFailed] = useState<Boolean>(false)
  const [matchCenterWidgetData, setMatchCenterWidgetData] = useState<IWidgetResponse | null>(null) // TODO refactor in future. Remove matchCenterWidgetData from League Center
  const [widgetLanguageId, setWidgetLanguageId] = useState<null | number>(null);
  const containerWidth = useCheckContainerWidth(elem);
  const isMobile = useCheckMobileScreen(elem);
  const {globalState, globalStateDispatch} = useGlobalState()

  const getData = () => {
    setLoading(true)
    fetchMatchCenterWidgetData(widgetID).then((r: IWidgetResponse) => {
      globalStateDispatch({type: 'setWidget', payload: r})
      globalStateDispatch({type: 'setIfHasCache', payload: hasCache})
      setUpTranslations(r.translations)
      setUpWidgetMetas(r.widgetMetas)
      setAllBuilderComponentStatuses(r.builderComponentGroup)
      setWidgetLanguageId(r.languageId);
      setWidgetProviders(r.widgetProviders);
      fetchBanners(widgetID).then((bannerAdRes: IBannerAdObj[]) => {
        const widgetWithBannerInfo = setBannerAdInfo(bannerAdRes, r);
        const orderedWidget = setPositionOrder(widgetWithBannerInfo);
        setMatchCenterWidgetData(orderedWidget);
        setLoading(false)
      })
    })
    .catch(() => {
      setRequestFailed(true)
    })
  }

  // Load widget data
  useEffect(getData, [hasCache, widgetID, competitionId, globalStateDispatch]);
  // Apply styling based on widget promoted provider
  useEffect(() => {
    if (globalState.widget !== null) {
      let widgetStyles = globalState.widget.widgetStyles;
      fetchPromotedProvider({
        widgetId: globalState.widget.id,
        competitionId: competitionId ?? globalState.widget.widgetCompetitions[0].competitionId
      }).then((r) => {
        if (r) {
          globalStateDispatch({type: 'setPromotedProvider', payload: r})
          if (r.provider.themeColor && widgetStyles !== undefined) {
            widgetStyles = setStyleVariable(MCStyleVariable.MainThemeColor, r.provider.themeColor, widgetStyles);
          }
        }
      }).catch((error) => {
        console.log(error)
      }).finally(() => {
        if (widgetStyles !== undefined)
          applyCustomStyling(widgetStyles);
      });
    }
  }, [competitionId, globalStateDispatch, globalState.widget]);

  return (
    <>
      {!hasCache && loading ? <LoadingBlock height={350} heightMobile={300} isMobile={isMobile}/> : (
        !requestFailed && (
          <div className={`${bem.e('wrapper')} ${(isMobile) ? bem.e('mobile') : ''}`} style={isMobile ? {minHeight: '315px'} : {}}>
            {matchCenterWidgetData && (
              <>
                {matchCenterWidgetData.builderComponentGroup.map((builderComponentGroup, index) => {
                  return (
                    <React.Fragment key={index}>
                      {builderComponentGroup.widgetBuilderComponent.map((widgetBuilderComponent, index) => {
                        switch (widgetBuilderComponent.builderComponent.slug) {
                          case BuilderComponentSlug.adBanner:
                            return (
                              <div className={bem.e('section-row')} key={index}
                                   id={widgetBuilderComponent.builderComponent.slug}>
                                <AdBanner
                                  bannerInfo={widgetBuilderComponent.widgetBanner}
                                  isMobile={isMobile}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.leagueOddsComparsion:
                            return (
                              <div
                                className={bem.e('section-row')} key={index}
                                id={widgetBuilderComponent.builderComponent.slug}
                              >
                                <OddsComparison
                                  widgetID={widgetID}
                                  bannerCodeDesktop={getMeta(WidgetMetaTypes.bannerCodeDesktop) ?? undefined}
                                  bannerCodeMobile={getMeta(WidgetMetaTypes.bannerCodeMobile) ?? undefined}
                                  elem={elem}
                                  competitionId={competitionId}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.leagueOutrightOdds:
                            return (
                              <div
                                className={bem.e('section-row')} key={index}
                                id={widgetBuilderComponent.builderComponent.slug}
                              >
                                <OutrightOdds
                                  componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                                  widgetId={widgetID}
                                  isMobile={isMobile}
                                  competitionId={competitionId}
                                  listRangeFrom={listRangeFrom}
                                  listRangeTo={listRangeTo}
                                  marketTypeIds={marketTypeIds}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.leagueStandings:
                            return widgetLanguageId ? (
                              <div
                                className={bem.e('section-row')} key={index}
                                id={widgetBuilderComponent.builderComponent.slug}
                              >
                                <Standings
                                  componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                                  languageID={widgetLanguageId}
                                  isMobile={isMobile}
                                  competitionId={matchCenterWidgetData.widgetCompetitions[0].competitionId}
                                  tournaments={matchCenterWidgetData.tournaments}
                                  oddsFormat={matchCenterWidgetData.oddsFormat}
                                  widgetId={widgetID}
                                  outLinkFolder={matchCenterWidgetData.channelOutLinkFolder}
                                  containerWidth={containerWidth}
                                />
                              </div>
                            ) : (<></>)
                          case BuilderComponentSlug.nextRoundBanner:
                            return (
                              <div className={bem.e('section-row')} key={index}
                                   id={widgetBuilderComponent.builderComponent.slug}>
                                <NextRoundBanner
                                  outLinkFolder={matchCenterWidgetData.outLinkFolder}
                                  isMobile={isMobile}
                                  widgetId={widgetID}
                                  competitionId={matchCenterWidgetData.widgetCompetitions[0].competitionId}
                                  widgetTypeId={matchCenterWidgetData.widgetTypeId}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.featuredEvents:
                            return (
                              <div
                                key={widgetBuilderComponent.id}
                                id={widgetBuilderComponent.builderComponent.slug}
                              >
                                <FeaturedEvents
                                  componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                                  eventID={matchCenterWidgetData.widgetCompetitions[0].competitionId}
                                  isMobile={isMobile}
                                  languageID={matchCenterWidgetData.languageId}
                                  oddsFormat={matchCenterWidgetData.oddsFormat}
                                  widgetId={matchCenterWidgetData.id}
                                  builderComponentId={widgetBuilderComponent.builderComponent.id}
                                  outLinks={matchCenterWidgetData.outLinks}
                                  allowedProviders={matchCenterWidgetData.widgetProviders}
                                  linkTemplate={matchCenterWidgetData.linkTemplate}
                                  isLeagueCenter={true}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.matchSchedule:
                            return (
                              <div
                                key={widgetBuilderComponent.id}
                                id={widgetBuilderComponent.builderComponent.slug}
                                className={bem.e('item')}
                              >
                                <MatchSchedule
                                  componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                                  allowedProviders={matchCenterWidgetData.widgetProviders}
                                  competitionIds={[matchCenterWidgetData.widgetCompetitions[0].competitionId]}
                                  isMobile={isMobile}
                                  languageID={widgetLanguageId ? widgetLanguageId : 0}
                                  builderComponentId={widgetBuilderComponent.builderComponent.id}
                                  outLinks={matchCenterWidgetData.outLinks}
                                  oddsFormat={matchCenterWidgetData.oddsFormat}
                                  containerWidth={containerWidth}
                                />
                              </div>
                            )
                          case BuilderComponentSlug.leagueCountdown:
                            return(
                              <div
                                key={widgetBuilderComponent.id}
                                id={widgetBuilderComponent.builderComponent.slug}
                              >
                                <LeagueCountdown
                                  metas={widgetBuilderComponent.widgetBuilderComponentMetas!}
                                />
                              </div>
                            )
                          default:
                            return null;
                        }
                      })}
                    </React.Fragment>
                  )
                })}
              </>
            )}
          </div>
        ))
      }
    </>
  )
}
export default LeagueCenter;
